import { useEffect, useState } from 'react';

/**
 * Hook that monitors the clipboard for a component with a 'Type' property.
 * Automatically validates that clipboard content is a valid JSON object with a Type field.
 * Updates when:
 * - Component mounts
 * - Window regains focus (detects external clipboard changes)
 * - User performs paste action (Ctrl+V / Cmd+V) anywhere in the window
 * - Clipboard copy event occurs in the window
 * - User clicks anywhere in the window (detects external clipboard changes)
 * 
 * @returns The parsed component from clipboard, or null if invalid/empty
 */
export function useClipboardComponent<T extends { Type: string }>(): T | null {
    const [clipboardComponent, setClipboardComponent] = useState<T | null>(null);

    useEffect(() => {
        const checkClipboard = async () => {
            try {
                const text = await navigator.clipboard.readText();
                const parsed = JSON.parse(text);
                if (parsed && typeof parsed === 'object' && 'Type' in parsed) {
                    setClipboardComponent(parsed as T);
                } else {
                    setClipboardComponent(null);
                }
            } catch {
                setClipboardComponent(null);
            }
        };

        // Check clipboard on mount, focus, paste, copy, and click events
        checkClipboard();
        window.addEventListener('focus', checkClipboard);
        window.addEventListener('paste', checkClipboard);
        window.addEventListener('copy', checkClipboard);
        window.addEventListener('click', checkClipboard);

        return () => {
            window.removeEventListener('focus', checkClipboard);
            window.removeEventListener('paste', checkClipboard);
            window.removeEventListener('copy', checkClipboard);
            window.removeEventListener('click', checkClipboard);
        };
    }, []);

    return clipboardComponent;
}
